import { useState, useEffect, useCallback } from 'react';
import { messageService, ConversationWithDetails } from '@/services/messageService';
import { Conversation, Message } from '@/integrations/mysql/types';
import { useAuthState } from './useAuthState';

export const useMessages = () => {
  const [conversations, setConversations] = useState<ConversationWithDetails[]>([]);
  const [currentMessages, setCurrentMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, userId, userType } = useAuthState();

  // Carregar conversas do usuário
  const loadConversations = useCallback(async () => {
    if (!isAuthenticated || !userId || !userType) {
      setConversations([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userConversations = await messageService.getConversations(userId);
      setConversations(userConversations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar conversas');
      console.error('Erro ao carregar conversas:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userId, userType]);

  // Carregar mensagens de uma conversa específica
  const loadMessages = useCallback(async (conversationId: number) => {
    setLoading(true);
    setError(null);

    try {
      const messages = await messageService.getMessages(conversationId);
      setCurrentMessages(messages);
      
      // Marcar mensagens como lidas
      if (userId) {
        await messageService.markMessagesAsRead(conversationId, userId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar mensagens');
      console.error('Erro ao carregar mensagens:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Enviar mensagem
  const sendMessage = useCallback(async (
    conversationId: number, 
    message: string
  ): Promise<Message | null> => {
    if (!userId || !userType) {
      setError('Usuário não autenticado');
      return null;
    }

    try {
      const newMessage = await messageService.sendMessage(
        conversationId,
        userId,
        message
      );

      // Atualizar mensagens locais
      setCurrentMessages(prev => [...prev, newMessage]);
      
      // Atualizar conversas para refletir a última mensagem
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, updated_at: new Date().toISOString(), last_message: newMessage }
          : conv
      ));

      return newMessage;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar mensagem');
      console.error('Erro ao enviar mensagem:', err);
      return null;
    }
  }, [userId, userType]);

  // Criar ou obter conversa
  const getOrCreateConversation = useCallback(async (
    propertyId: number, 
    guestId: number, 
    ownerId: number
  ): Promise<Conversation | null> => {
    try {
      const conversation = await messageService.createConversation(
        propertyId, 
        guestId, 
        ownerId
      );
      
      // Adicionar à lista de conversas se não existir
      setConversations(prev => {
        const exists = prev.some(conv => conv.id === conversation.id);
        if (!exists) {
          return [conversation, ...prev];
        }
        return prev;
      });

      return conversation;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar conversa');
      console.error('Erro ao criar conversa:', err);
      return null;
    }
  }, []);

  // Obter contagem de mensagens não lidas
  const getUnreadCount = useCallback(async (): Promise<number> => {
    if (!userId) return 0;

    try {
      // Por enquanto, retornar 0 (funcionalidade de mensagens não lidas pode ser implementada depois)
      return 0;
    } catch (err) {
      console.error('Erro ao obter contagem de não lidas:', err);
      return 0;
    }
  }, [userId]);

  // Carregar conversas quando o usuário mudar
  useEffect(() => {
    if (isAuthenticated && userId && userType) {
      loadConversations();
    } else {
      setConversations([]);
      setCurrentMessages([]);
    }
  }, [isAuthenticated, userId, userType, loadConversations]);

  return {
    conversations,
    currentMessages,
    loading,
    error,
    loadConversations,
    loadMessages,
    sendMessage,
    getOrCreateConversation,
    getUnreadCount,
  };
};
